// Match the original SDK prefix so existing browser records remain readable.
const PREFIX = '__miaoda_app_17f4v1fxkr3__:';
import { isUserDataKey } from './userDataKeys';
let userCache: { prefix: string; values: Map<string, string | null>; persist: (key: string, value: string | null) => void; merge: (key: string, incoming: string | null, local: string | null) => string | null } | undefined;
export function activateUserCache(prefix: string, values: Record<string, string | null>, persist: (key: string, value: string | null) => void,
  merge: (key: string, incoming: string | null, local: string | null) => string | null) {
  const cache = { prefix, values: new Map(Object.entries(values)), persist, merge };
  userCache = cache;
  return () => { if (userCache === cache) userCache = undefined; };
}
function cachedKey(key: string): string | undefined {
  if (userCache && key.startsWith(userCache.prefix)) {
    const business = key.slice(userCache.prefix.length);
    if (isUserDataKey(business)) return business;
  }
}
type StorageListener = (key: string, source?: 'external') => void;
const listeners = new Set<StorageListener>();
export function onStorageWrite(listener: StorageListener) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
const channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel('lianke-account-cache-v1') : undefined;
function externalWrite(key: string, value: string | null) {
  const business = cachedKey(key);
  if (business === undefined || !userCache) return;
  writeCloudCache(key, userCache.merge(business, value, scopedStorage.getItem(key)));
  listeners.forEach(listener => listener(key, 'external'));
}
if (channel) channel.onmessage = event => {
  const data = event.data;
  if (typeof data?.key === 'string' && (data.value === null || typeof data.value === 'string')) externalWrite(data.key, data.value);
};
window.addEventListener('storage', event => {
  if (event.storageArea === localStorage && event.key?.startsWith(PREFIX)) {
    if (!channel) externalWrite(event.key.slice(PREFIX.length), event.newValue);
  }
});
// Cloud hydration bypasses local-edit notifications, and never clears another account.
export function writeCloudCache(key: string, value: string | null, mirror = false) {
  const business = cachedKey(key);
  if (business !== undefined && userCache) {
    userCache.values.set(business, value);
    userCache.persist(business, value);
    // IndexedDB is authoritative for active account data; localStorage is a compatibility mirror.
    if (mirror) try {
      if (value === null) localStorage.removeItem(PREFIX + key);
      else localStorage.setItem(PREFIX + key, value);
    } catch { /* Large photo collections are durably stored in IndexedDB instead. */ }
  } else if (value === null) localStorage.removeItem(PREFIX + key);
  else localStorage.setItem(PREFIX + key, value);
}

function keys(): string[] {
  const result: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.startsWith(PREFIX)) result.push(key.slice(PREFIX.length));
  }
  if (userCache) for (const [key, value] of userCache.values) {
    const full = userCache.prefix + key;
    if (value !== null && !result.includes(full)) result.push(full);
    if (value === null && result.includes(full)) result.splice(result.indexOf(full), 1);
  }
  return result;
}

export const scopedStorage = {
  getItem(key: string) {
    const business = cachedKey(key);
    if (business !== undefined && userCache?.values.has(business)) return userCache.values.get(business) ?? null;
    return localStorage.getItem(PREFIX + key);
  },
  setItem(key: string, value: string) {
    if (scopedStorage.getItem(key) === value) return;
    writeCloudCache(key, value, true);
    listeners.forEach(listener => listener(key));
    channel?.postMessage({ key, value });
  },
  removeItem(key: string) {
    if (scopedStorage.getItem(key) === null) return;
    writeCloudCache(key, null, true);
    listeners.forEach(listener => listener(key));
    channel?.postMessage({ key, value: null });
  },
  clear() { keys().forEach(key => scopedStorage.removeItem(key)); },
  key(index: number) { return keys()[index] ?? null; },
  get length() { return keys().length; },
};
